import { Link } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { LOGO_URL, ORG, FOUNDER_IMAGE } from "../lib/constants";
import { fetchEvents } from "../lib/api";
import { useEffect, useState } from "react";
import {
  ArrowRight,
  HeartHandshake,
  Home as HomeIcon,
  Church,
  Sparkles,
  BookOpen,
  Users,
  Calendar,
  Clock,
  MapPin,
} from "lucide-react";

export default function HomePage() {
  const [events, setEvents] = useState([]);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  useEffect(() => {
    fetchEvents().then(setEvents).catch(() => setEvents([]));
  }, []);
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-cream via-brand-bone/40 to-brand-cream pointer-events-none" />
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-brand-peach/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-24 w-[420px] h-[420px] rounded-full bg-brand-teal/10 blur-3xl pointer-events-none" />

        <div className="container-editorial relative pt-20 pb-24 md:pt-28 md:pb-36">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <Reveal className="md:col-span-7 animate-fade-up">
              <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold">
                <span className="w-6 h-px bg-brand-rose" /> A Place of Hope &amp; Restoration
              </span>
              <h1 className="mt-5 font-serif text-5xl sm:text-6xl lg:text-[76px] leading-[1.02] text-brand-charcoal tracking-tight">
                Restoring lives.
                <br />
                <span className="italic text-brand-teal">Rebuilding</span> futures.
              </h1>
              <p className="mt-6 text-lg text-brand-muted max-w-xl leading-relaxed">
                Hosea Ministries Foundation is a Christ-centered transitional home in Tahlequah, Oklahoma, providing
                safe housing, faith, structure, and sisterhood to women rebuilding their lives after incarceration
                or addiction.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to="/apply"
                  data-testid="hero-apply-btn"
                  className="inline-flex items-center gap-2 bg-brand-teal text-white rounded-full px-7 py-4 font-medium hover:bg-brand-tealDark transition-all hover:-translate-y-0.5 shadow-sm hover:shadow-xl"
                >
                  Apply to the program <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href={ORG.donateUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="hero-donate-btn"
                  className="inline-flex items-center gap-2 border-2 border-brand-teal text-brand-teal rounded-full px-7 py-4 font-medium hover:bg-brand-teal hover:text-white transition-all"
                >
                  Support our mission
                </a>
              </div>
              <div className="mt-10 flex items-center gap-6 text-xs text-brand-muted">
                <div>
                  <div className="font-serif text-2xl text-brand-teal">1 year</div>
                  <div className="uppercase tracking-wider">Program length</div>
                </div>
                <div className="w-px h-8 bg-brand-line" />
                <div>
                  <div className="font-serif text-2xl text-brand-teal">Aftercare</div>
                  <div className="uppercase tracking-wider">Continued support</div>
                </div>
                <div className="w-px h-8 bg-brand-line" />
                <div>
                  <div className="font-serif text-2xl text-brand-teal">Tahlequah</div>
                  <div className="uppercase tracking-wider">NE Oklahoma</div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={200} className="md:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 rounded-[2.5rem] bg-brand-teal/5 rotate-[-2deg]" />
                <div className="relative rounded-[2rem] overflow-hidden bg-brand-bone aspect-[4/5] shadow-xl">
                  <img
                    src={FOUNDER_IMAGE}
                    alt="Nikki Hendricks — Founder of Hosea Ministries Foundation"
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-lg p-5 max-w-[260px] border border-brand-line">
                  <div className="text-[10px] tracking-[0.24em] uppercase text-brand-rose font-semibold">
                    Founder
                  </div>
                  <div className="font-serif text-xl text-brand-charcoal mt-1">Nikki Hendricks</div>
                  <div className="text-xs text-brand-muted mt-2 italic leading-relaxed">
                    “I will lead her into the wilderness and speak tenderly to her.”
                    <span className="not-italic block text-[10px] tracking-[0.2em] uppercase text-brand-rose/80 mt-1">Hosea 2:14</span>
                  </div>
                </div>
                <img
                  src={LOGO_URL}
                  alt=""
                  className="absolute -top-5 -right-5 w-20 h-20 rounded-full bg-white shadow-md p-2"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section id="mission" className="py-24 md:py-32">
        <div className="container-editorial">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <Reveal className="md:col-span-5">
              <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold">Our Mission</div>
              <h2 className="mt-3 font-serif text-4xl md:text-5xl text-brand-charcoal leading-tight tracking-tight">
                Safe housing &amp; support for women walking into new life.
              </h2>
            </Reveal>
            <Reveal delay={120} className="md:col-span-7 md:pt-6">
              <p className="text-lg text-brand-muted leading-relaxed">
                {ORG.mission}
              </p>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { icon: HomeIcon, label: "Safe Housing" },
                  { icon: BookOpen, label: "Life Skills" },
                  { icon: Church, label: "Faith & Fellowship" },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-3 bg-brand-cream rounded-xl px-4 py-4 border border-brand-line">
                    <span className="w-9 h-9 rounded-full bg-brand-teal/10 text-brand-teal flex items-center justify-center">
                      <Icon className="w-4 h-4" />
                    </span>
                    <span className="text-sm font-medium text-brand-charcoal">{label}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="py-20 bg-white border-y border-brand-line">
        <div className="container-editorial">
          <Reveal>
            <div className="max-w-2xl">
              <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold">What We Do</div>
              <h2 className="mt-3 font-serif text-4xl md:text-5xl text-brand-charcoal tracking-tight">
                A whole-life program, rooted in faith.
              </h2>
              <p className="mt-4 text-brand-muted leading-relaxed">
                Our one-year program surrounds each resident with housing, counseling, spiritual formation, and
                practical support — then continues with aftercare.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: HomeIcon,
                title: "Transitional Living",
                body:
                  "A safe, structured home in Tahlequah where women can focus on healing and restoration alongside a resident house manager.",
              },
              {
                icon: HeartHandshake,
                title: "Counseling & Recovery",
                body:
                  "Quality counseling through Phoenix Counseling, weekly Celebrate Recovery, and accountability built into daily life.",
              },
              {
                icon: Users,
                title: "Community & Resources",
                body:
                  "Partnerships with the McIntosh Foundation, local churches, and community connections for jobs, education, and childcare.",
              },
              {
                icon: Church,
                title: "Faith Formation",
                body:
                  "Weekly church services and Bible study with local churches that welcome, encourage, and disciple.",
              },
              {
                icon: BookOpen,
                title: "Life & Parenting Skills",
                body:
                  "Practical training in housekeeping, budgeting, parenting, and the rhythms of healthy daily living.",
              },
              {
                icon: Sparkles,
                title: "Aftercare",
                body:
                  "When the year ends, our care doesn't. Graduates continue to receive ongoing support and sisterhood.",
              },
            ].map((s, i) => (
              <Reveal key={s.title} delay={i * 80}>
                <div className="h-full bg-brand-cream rounded-2xl p-7 border border-brand-line hover:-translate-y-1 hover:shadow-lg transition-all duration-500 group">
                  <span className="w-11 h-11 rounded-xl bg-brand-teal/10 text-brand-teal flex items-center justify-center group-hover:bg-brand-teal group-hover:text-white transition-colors">
                    <s.icon className="w-5 h-5" />
                  </span>
                  <h3 className="mt-5 font-serif text-2xl text-brand-charcoal">{s.title}</h3>
                  <p className="mt-3 text-[15px] text-brand-muted leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-12 flex justify-center">
            <Link
              to="/programs"
              data-testid="learn-programs-btn"
              className="inline-flex items-center gap-2 text-brand-teal font-medium link-underline"
            >
              Read full program details <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section id="founder" className="py-24 md:py-32">
        <div className="container-editorial">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <Reveal className="md:col-span-5">
              <div className="relative">
                <div className="absolute -inset-3 rounded-[2rem] bg-brand-peach/25 rotate-2" />
                <div className="relative rounded-[1.75rem] overflow-hidden aspect-[3/4] bg-brand-bone shadow-lg">
                  <img
                    src={FOUNDER_IMAGE}
                    alt="Nikki Hendricks — Founder of Hosea Ministries Foundation"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </Reveal>
            <Reveal delay={120} className="md:col-span-7">
              <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold">Meet the Founder</div>
              <h2 className="mt-3 font-serif text-4xl md:text-5xl text-brand-charcoal tracking-tight leading-tight">
                Nikki Hendricks — a heart for women ready to come home.
              </h2>
              <p className="mt-5 text-lg text-brand-muted leading-relaxed">
                Rooted in faith and in her own walk of restoration, Nikki founded Hosea Ministries to answer a deep
                need in rural northeast Oklahoma — a place for women leaving incarceration or addiction to find
                shelter, sisterhood, and a steady path forward.
              </p>
              <blockquote className="mt-7 border-l-2 border-brand-peach pl-5 italic font-serif text-xl text-brand-charcoal">
                “I will lead her into the wilderness and speak tenderly to her… I will make the Valley of Achor a door of hope.”
                <span className="not-italic block font-sans text-[11px] tracking-[0.24em] uppercase text-brand-rose/80 mt-3">Hosea 2:14–15</span>
              </blockquote>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/about"
                  data-testid="founder-learn-more"
                  className="inline-flex items-center gap-2 text-brand-teal font-medium link-underline"
                >
                  Read our story <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href={`tel:${ORG.phoneTel}`}
                  data-testid="founder-call"
                  className="inline-flex items-center gap-2 text-brand-charcoal font-medium link-underline"
                >
                  Call Nikki · {ORG.phone}
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* EVENTS */}
      {events.length > 0 && (
        <section id="events" className="py-20 bg-brand-teal text-brand-cream relative overflow-hidden">
          <div className="absolute inset-0 grain-overlay opacity-40 pointer-events-none" />
          <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-brand-peach/20 blur-3xl" />
          <div className="container-editorial relative">
            <Reveal className="max-w-2xl">
              <div className="text-[11px] tracking-[0.24em] uppercase text-brand-peach font-semibold">Upcoming Events</div>
              <h2 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight">
                Come and gather.
              </h2>
              <p className="mt-4 text-brand-cream/80 leading-relaxed">
                Find us at one of these moments to celebrate, support, and walk alongside the women in our care.
              </p>
            </Reveal>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
              {events.map((ev, i) => {
                const imgSrc = ev.image_url
                  ? (ev.image_url.startsWith("http") ? ev.image_url : `${BACKEND_URL}${ev.image_url}`)
                  : null;
                return (
                  <Reveal key={ev.id} delay={i * 80}>
                    <article
                      data-testid={`event-card-${ev.id}`}
                      className="bg-white/5 backdrop-blur border border-white/10 rounded-3xl overflow-hidden hover:-translate-y-1 hover:bg-white/10 transition-all duration-500 h-full flex flex-col"
                    >
                      {imgSrc && (
                        <div className="aspect-[16/9] overflow-hidden">
                          <img src={imgSrc} alt={ev.title} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="p-7 flex-1 flex flex-col">
                        <h3 className="font-serif text-2xl tracking-tight">{ev.title}</h3>
                        {ev.description && (
                          <p className="mt-3 text-sm text-brand-cream/80 leading-relaxed">{ev.description}</p>
                        )}
                        <div className="mt-5 space-y-1.5 text-xs text-brand-cream/80">
                          {ev.date_label && (
                            <div className="inline-flex items-center gap-2 mr-4"><Calendar className="w-3.5 h-3.5 text-brand-peach" /> {ev.date_label}</div>
                          )}
                          {ev.time_label && (
                            <div className="inline-flex items-center gap-2 mr-4"><Clock className="w-3.5 h-3.5 text-brand-peach" /> {ev.time_label}</div>
                          )}
                          {ev.location && (
                            <div className="inline-flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-brand-peach" /> {ev.location}</div>
                          )}
                        </div>
                        {ev.rsvp_url && (
                          <a
                            href={ev.rsvp_url}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`event-rsvp-${ev.id}`}
                            className="mt-6 self-start inline-flex items-center gap-2 bg-brand-peach text-brand-charcoal rounded-full px-5 py-2.5 text-sm font-medium hover:bg-white transition-all hover:-translate-y-0.5"
                          >
                            RSVP <ArrowRight className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </article>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24">
        <div className="container-editorial">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-brand-bone to-brand-cream border border-brand-line px-8 md:px-16 py-16 md:py-20">
              <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-brand-peach/20 blur-3xl pointer-events-none" />
              <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                <div className="md:col-span-8">
                  <h2 className="font-serif text-4xl md:text-5xl text-brand-charcoal tracking-tight leading-tight">
                    Ready to come home?
                  </h2>
                  <p className="mt-4 text-lg text-brand-muted max-w-2xl">
                    Our online application makes starting the process simple. We'll receive your story, review it
                    carefully, and respond within 72 hours.
                  </p>
                </div>
                <div className="md:col-span-4 md:text-right">
                  <Link
                    to="/apply"
                    data-testid="cta-apply-btn"
                    className="inline-flex items-center gap-2 bg-brand-teal text-white rounded-full px-7 py-4 font-medium hover:bg-brand-tealDark transition-all hover:-translate-y-0.5 shadow-sm hover:shadow-xl"
                  >
                    Begin your application <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
